import { sampleStateLabel } from './sampleModel.ts'
import { useId, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useParams } from 'react-router'

import type { Sample } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { SAMPLES } from '@ctcells/fixtures'
import { enumLabel, maskPii, t } from '@ctcells/i18n'

import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { Button } from '../../atoms/Button.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Field } from '../../atoms/Field.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { canView, gate, viewGate, type ActionGate, type ScreenAccess } from '../access.ts'
import { dateTimeText, moduleLabelOf, orDash } from '../format.ts'
import { Card, DetailPage, OfflineNotice } from '../resourceParts.tsx'
import {
  correctionOf,
  draftOf,
  type CorrectionDraft,
  type SampleCorrection,
} from './sampleCorrection.ts'
import { canReregisterSample } from './sampleReregister.ts'
import { sampleHints, sampleTypeHintLabel } from './sampleFormatHints.ts'
import { SampleHintInput } from './SampleHintInput.tsx'
import { SampleReportModal, type SampleReportModalProps } from './SampleReportModal.tsx'
import { sampleIdentityHeadRows, sampleTestInfoRows } from './sampleIdentityRows.ts'

import '../screens.css'
import '../resource.css'
import './sampleHint.css'
import './sampleDetail.css'

/**
 * 시료 상세 · 정정 — 와이어프레임 `samples()` 상세 2444행.
 *
 * 계약: 정정은 `PUT /samples/{id}` + **`reason` 필수**(감사 기록). `status` · `batch_id` ·
 * `current_module` · `current_position` · `discard_reason` 은 상태기계 소유라 화면이 못 고친다.
 *
 * 🔴 마스킹은 `view` 권한으로 가른다(`service` 는 `view` 가 없다 · §5-5).
 *
 * 🔴 **정정 입력 여섯이 실제로 서버로 간다**(2026-09-21 수정). 종전에는 여섯 칸이 전부
 *    `onChange={() => undefined}` 라 **적어도 값이 아무 데도 안 남았고** 나가는 것은 `reason`
 *    하나였다. 받는 척하고 버리는 입력이라 「정정했다」고 믿은 사람이 틀린 값을 그대로 뒀다.
 *    ⇒ 서버가 받는 칸은 `CorrectSampleBody` 가 정한다(실서버 `/openapi.json` 실측):
 *    `patient_id` · `sample_type` · `rbc_concentration` · `blood_draw_date` · `cancer_type` ·
 *    `cancer_stage` · `metadata`. **`sample_id` 는 없다** — 등록 때만 정한다.
 * 🔴 **바뀐 칸만 보낸다.** 서버가 `body.model_fields_set` 로 「보낸 것」만 적용하므로
 *    (`app/views/samples.py`) 안 보낸 칸은 그대로 남는다. 전부 보내면 안 고친 칸까지
 *    감사 원장의 `before/after` 에 실린다.
 * ⚠ `metadata`(자유 항목)는 **화면에 자리가 없다.** 편집기가 없는 값을 보내지 않는다.
 * ⚠ 2026-10-23 — 적혈구 농도(`rbc_concentration`) 입력 · 보기 줄을 걷었다(시료 추가 창 기준 · `sampleTestInfoRows`). 정정 입력은 다섯이다.
 */
export interface SampleDetailScreenProps {
  saveError?: string | undefined
  preparationDrawer?: boolean | undefined
  samples?: readonly Sample[]
  access?: ScreenAccess
  /** 「시험용으로 다시 등록」 — 누르면 컨테이너가 창 없이 바로 `POST /samples` 를 보낸다(eric 2026-10-29) */
  onReregister?: (sampleId: string) => void
  reregisterGate?: ActionGate
  /** 다시 등록 실패 — 서버 문구 그대로. 머리 아래에 선다 */
  reregisterError?: string | undefined
  /** 짧은 성공 알림 — 다시 등록으로 막 열린 새 시료의 서랍에서만 넘긴다 */
  notice?: string | undefined
  onSave?: (sampleId: string, patch: SampleCorrection, reason: string) => void
  /** 서랍으로 열렸을 때의 닫기(×). 없으면 「← 시료」 줄이다 */
  onClose?: (() => void) | undefined
  /**
   * 「보고서」 — 컨테이너가 `GET /reports/{sample_id}` 를 연다(조회뿐).
   * 🔴 **없으면(demo) 비활성 + 사유다** — 누를 수 있는데 아무 일도 없는 버튼을 두지 않는다.
   */
  onOpenReport?: ((sampleId: string) => void) | undefined
  /** 열린 보고서 대화 — 상태는 컨테이너가 쥔다. 없으면 대화가 닫혀 있다 */
  reportPanel?: Omit<SampleReportModalProps, 'view'> | undefined
  /**
   * **「보고서」 버튼을 두지 않는다** — 모니터링 · 통합콘솔 시료 목록 서랍이 켠다(eric 2026-10-02 「보고서 버튼은 빼고.. 정정 버튼은
   * 타이틀 오른쪽 정렬 쪽에」). 켜면 「정정」이 **제목 줄 오른쪽**에 선다(정정 중의 취소 · 정정 저장은 종전대로 아래 줄) —
   * 서랍 머리의 「단추는 아래 줄」(`layout.css`)을 이 머리에서만 푼다(`sampleDetail.css`).
   */
  hideReport?: boolean | undefined
  /**
   * **「정정」 버튼을 두지 않는다** — 모니터링 시료 서랍이 켠다(eric 2026-10-06 「정정 버튼 삭제하고」). 보는 화면이다 —
   * 정정은 시료 목록 서랍(작업 준비 › 시료)에서 한다. 🔴 시료 목록 서랍은 끄지 마라(정정으로 가는 길이 그 버튼 하나다).
   */
  hideCorrection?: boolean | undefined
  /**
   * **상태를 칩으로** — 테두리 없이 면을 채운 알약(제목 앞 순번 칩 「시료 2」와 같은 결 · eric 2026-10-06 「진행 중.. 칩으로」).
   * 글자 · 상태 색은 그대로다(`StatusBadge`). 모니터링 시료 서랍이 켠다.
   */
  statusChip?: boolean | undefined
  /** **「자리」 구역을 「시료」 구역 위로** — 모니터링 시료 서랍이 켠다(eric 2026-10-06 「자리 섹션을 위로.. 시료 섹션 위로」) */
  placementFirst?: boolean | undefined
  /** 「자리」 구역의 이름 — 모니터링 챔버 상세가 「위치」로 넘긴다(eric 2026-10-06). 안 넘기면 「자리」 */
  placementTitle?: string | undefined
  /**
   * **서랍 본문 «위»에 얹는 구역** — 모니터링 시료 서랍이 그 시료의 챔버 3D 를 여기 세운다(2026-09-28).
   * 🔴 서랍(`onClose`)일 때만 그린다. 안 넘기면 본문 DOM 이 종전과 한 칸도 다르지 않다(시료 목록 서랍).
   */
  lead?: ReactNode
  /** 서랍 본문 «아래»에 얹는 구역 — 모니터링 시료 서랍의 진행 · 분주 기록. 규칙은 `lead` 와 같다 */
  trail?: ReactNode
  /**
   * 제목 앞 글자 — 모니터링 카드의 순번(「시료 1」). 🔴 **그 화면 안의 차례**라 모니터링 서랍만 넘긴다.
   * 안 넘기면 제목은 시료 번호 하나다(종전).
   */
  titleLead?: string | undefined
  /**
   * 제목의 시료 번호 자리를 대신하는 글자 — 모니터링 카드가 레시피 시료 묶음(시료 둘)을 한 장으로 보일 때
   * 「TEST-…-07 · 08」 처럼 묶음의 번호 전부를 적는다(eric 2026-10-14 「시료 4에.. 두개가 보여야」). 안 넘기면 이 시료 번호다.
   */
  titleText?: string | undefined
  /** `identityInBody` 의 「시료」 카드 제목 — 묶음 서랍은 시료마다 카드가 서서 번호를 붙인다(「시료 · TEST-…-07」) */
  identityTitle?: string | undefined
  /**
   * 머리 부제에서 뺄 조각 — 연 화면이 이미 보이는 값(eric 2026-09-23 「row 에 보이는 거니 상세에선 다 빼」).
   * 모니터링은 작업(상시 작업 줄)과 단계(챔버 아래 한 마디)를 이미 말한다.
   */
  omitMeta?: readonly SampleMetaPiece[] | undefined
  /**
   * **보기 상태에서 시료 정보를 본문 첫 구역으로** — 환자 번호 · 종류 · 적혈구 농도 · 채혈 날짜 · 암종 · 암 병기를
   * 「시료」 카드 한 장에 정정 폼과 같은 이름 · 같은 순서로 적는다(값 있는 줄만).
   * eric 2026-09-28 「시료를 클릭하면 해당 시료의 정보가 나와야 하는 거 아니야? 혈액 환자.. 무슨 암..」 —
   * 모니터링 카드에는 환자 번호 · 종류가 없어 머리 부제 한 줄로는 안 읽힌다. 켜면 그 둘은 부제에서 빠진다.
   * ⚠ 시료 목록의 서랍은 끈다 — 목록 행이 환자 번호 · 종류를 이미 보인다(종전 그대로).
   */
  identityInBody?: boolean | undefined
  /**
   * **「배정 웰」 줄에 적을 묶음의 웰 전부** — 모니터링 서랍이 그 시료의 챔버(레시피 `sample_groups[].wells`)를 넘긴다
   * (eric 2026-09-28 「배정웰이 두개인데.. 왜 하나만 나와?」). 서버 `assigned_well` 은 시료마다 하나(작업을 만들 때 보낸
   * 대표 웰)라 챔버 둘을 다 말하지 못한다. 넘기면 줄은 「A1 · A2」, 서버 값은 툴팁(「서버 배정 A1」)으로 간다.
   * ⚠ 안 넘기거나 비면(또는 서버 값이 없거나 묶음 밖이면) 종전대로 서버 값 하나 — 시료 목록 서랍에는 레시피 · 묶음 자료가 없다.
   */
  assignedWells?: readonly string[] | undefined
  /**
   * 정정 폼의 종류 · 암종 · 암 병기 **제안 출처** — 이미 등록된 시료들(2026-10-01 회의 「직접 입력 + 이전 입력값 자동완성」).
   * 시료 추가 폼과 같은 `sampleHints` 로 거른다. 🔴 제안일 뿐이다 — 목록 밖 값도 그대로 나간다.
   * 안 넘기면 `samples` 에서 뽑는다. 목록을 못 받았으면(403 · 실패) 빈 배열 — 고정 제안만 남고 직접 입력은 그대로다.
   */
  hintSamples?: readonly Sample[] | undefined
}

/**
 * 머리 부제의 조각 중 부르는 쪽이 뺄 수 있는 것.
 * `module` · `registered` 는 **서랍일 때만** 붙는 조각이다(2026-09-28) — 시료 목록이 서랍을 열면 「현재 모듈」 ·
 * 「등록 시각」 열이 빠지는데(`listColumnPriority.ts`) 서랍에는 「운전」 카드가 없어 그 값이 갈 곳이 없었다.
 */
export type SampleMetaPiece = 'batch' | 'stage' | 'module' | 'registered' | 'version'

export function SampleDetailScreen({
  samples = SAMPLES,
  access = {},
  onSave,
  onClose,
  onOpenReport,
  onReregister,
  reregisterGate,
  reregisterError,
  notice,
  reportPanel,
  hideReport = false,
  preparationDrawer = false,
  saveError,
  hideCorrection = false,
  statusChip = false,
  placementFirst = false,
  placementTitle,
  lead,
  trail,
  titleLead,
  titleText,
  identityTitle,
  omitMeta = [],
  identityInBody = false,
  assignedWells,
  hintSamples,
}: SampleDetailScreenProps) {
  const { sampleId } = useParams()
  const sample = samples.find((s) => s.sample_id === sampleId)
  const [reason, setReason] = useState('')
  const bodyRef = useRef<HTMLDivElement>(null)
  /** `null` = 아직 아무것도 안 고쳤다. 값이 서버에서 새로 오면 그때 다시 초안을 만든다 */
  const [draft, setDraft] = useState<CorrectionDraft | null>(null)
  /**
   * 서랍에서 「정정」을 연 시료의 `시료@버전`. 🔴 **버전이 오르면 보기로 돌아온다** — 저장이
   * 성공해 서버가 새 판을 주면 그때 닫힌다(낙관적으로 먼저 닫지 않는다 · DP-04).
   * 다른 시료로 옮겨도 키가 달라 닫힌다.
   */
  const [editKey, setEditKey] = useState<string | null>(null)
  const v = useSubmitValidation((text: string) => ({
    reason: text.trim() === '' ? t('validation.correctionReasonRequired') : undefined,
  }))
  const fieldIds = useId()
  const hints = useMemo(() => sampleHints(hintSamples ?? samples), [hintSamples, samples])

  if (!sample) {
    return (
      <DetailPage
        backLabel={t('screen.samples.title')}
        backTo={PATHS.samples}
        onClose={onClose}
        title={t('screen.sampleDetail.title')}
      >
        <EmptyState label={t('state.empty.samples')} />
      </DetailPage>
    )
  }

  const visible = canView(access)
  /*
   * 🔴 **서랍이면 목록이 «왼쪽에 그대로 있다»**(eric 2026-09-23 「이건 row 에 보이는 거니
   *    상세에선 다 빼」). 행에 이미 보이는 값을 읽기 전용으로 또 적지 않는다.
   * 🔴 **전체 페이지(장비 관점 콘솔)에서는 다 보인다** — 거기엔 옆에 목록이 없다.
   *    `onClose` 가 그 둘을 가르는 유일한 축이다(서랍만 닫기를 넘긴다).
   * ⚠ **편집 입력칸은 값이 행에 있어도 남는다**(환자 번호 · 종류) — 표시가 아니라 정정 수단이다.
   */
  const inDrawer = onClose !== undefined
  const compactDrawer = inDrawer && preparationDrawer
  /*
   * 🔴 **서랍은 «보기»가 기본이다**(eric 2026-09-27 「없는 건 빼버려.. 저것들 필요해?」).
   *    값 있는 항목만 글자로 적고, 입력칸·「미입력」·정정 사유는 「정정」을 눌러야 연다.
   * ⚠ 전체 페이지(장비 관점 콘솔)는 종전대로 늘 정정 폼이다 — 그 앱은 이번 범위가 아니다.
   */
  const sampleKey = `${sample.sample_id}@${String(sample.version)}`
  const editing = !inDrawer || editKey === sampleKey
  const openEdit = () => {
    setDraft(null)
    setReason('')
    v.reset()
    setEditKey(sampleKey)
  }
  const closeEdit = () => {
    setDraft(null)
    setReason('')
    v.reset()
    setEditKey(null)
  }
  const done = sample.status === 'completed'
  const current = draft ?? draftOf(sample)
  const patch = correctionOf(sample, current, { patientIdEditable: visible })
  const changed = Object.keys(patch).length > 0

  /**
   * 정정은 control 권한 + 사유 필수. 사유가 비면 비활성 + 사유. 작업 준비 서랍은 하단에도 사유를 표시한다.
   * 🔴 **고친 칸이 없으면 못 누른다**(2026-09-21 추가). 값이 그대로인 채로 보내면 서버가
   *    바뀐 것 없이 200 을 주고 **감사행도 안 쓴다**(`if fields:` — `app/views/samples.py`).
   *    사람은 「정정이 기록됐다」고 믿는데 원장에는 아무것도 없는 상태가 된다.
   */
  const saveGate = gate(access, {
    perm: 'control',
    stateOk: changed,
    stateReason: 'reason.nothingChanged',
  })
  const fieldGate = gate(access, { perm: 'control' })
  /**
   * 🔴 환자 식별자는 **보이는 사람만** 고친다. 가려진 값(`••••`)을 고치게 두면 마스크
   *    문자열이 그대로 올라가 진짜 식별자를 덮는다 — 되돌릴 수 없는 정정이라 더 나쁘다.
   */
  const patientGate: typeof fieldGate = visible
    ? fieldGate
    : { disabled: true, disabledReason: t('reason.maskedValue') }

  const edit = (key: keyof SampleCorrection) => (value: string) =>
    setDraft({ ...current, [key]: value })

  /**
   * 본문 카드 세 장 — **서랍과 전체 페이지가 같은 것을 쓴다.** 두 벌로 적으면 한쪽만 고쳐진다.
   * 갈리는 것은 «오른쪽 열이 있는가»뿐이다(아래 `inDrawer` 갈래).
   */
  /* 검사 정보 넷 — 규칙은 `sampleTestInfoRows` 머리말 */
  const testInfoRows = sampleTestInfoRows(sample)
  const testInfoShown = identityInBody
    ? testInfoRows.filter((row) => row.value !== '')
    : testInfoRows.map((row) => ({
        key: row.key,
        label: row.label,
        value: row.value === '' ? <NaOr value={null} /> : row.value,
      }))
  /** 「시료」 카드 머리 두 줄(`identityInBody` 일 때만) — 정정 폼의 「시료」 카드와 같은 이름 · 같은 순서 */
  const identityRows = identityInBody ? sampleIdentityHeadRows(sample, visible) : []

  /*
   * 🔴 **시료 목록의 서랍 — 「시료」 카드가 환자 번호 · 작업 · 단계 · 현재 모듈을 적는다**(eric 2026-09-30 「시료 번호, 종류,
   *    처분, 등록 시각 이렇게 하고.. 환자 번호랑.. 작업은... 상세 페이지에 넣어줘」). 넷 다 목록 열에서 빠져 갈 곳이 서랍뿐이다.
   *    종전에는 머리 부제에 이름표 없는 조각으로 붙어 있어서(환자 번호 · 작업 앞 8자) 무슨 값인지 안 읽혔다 — 이름표를 단 줄로 옮겼다.
   *    머리 부제에서는 그 넷을 뺀다(한 값을 두 번 적지 않는다).
   * ⚠ 모니터링 서랍(`identityInBody`)은 종전 그대로다 — 거기는 작업 · 단계 · 현재 모듈을 화면이 이미 말한다(`omitMeta`).
   * ⚠ 전체 페이지(장비 관점 콘솔)도 종전 그대로 — 「운전」 카드가 같은 값을 적는다.
   * 값 있는 줄만 선다(완료면 현재 모듈 없음 · 수거 뒤 §16-8). 단계는 서버가 늘 싣는다.
   */
  const listDrawer = inDrawer && !identityInBody
  const listRows = listDrawer
    ? [
        {
          key: 'patient',
          label: t('column.patientId'),
          /* 선택 입력 칸 — 비었으면 「NA」(회의 2026-10-01). 아래 셋(작업 · 단계 · 모듈)은 입력이 아니라 상태라 그대로 뺀다 */
          value:
            textOf(sample.patient_id) === '' ? (
              <NaOr value={null} />
            ) : (
              <span className="mono">{maskPii(sample.patient_id, visible)}</span>
            ),
        },
        {
          key: 'batch',
          label: t('column.batch'),
          value:
            typeof sample.batch_id === 'string' && sample.batch_id !== '' ? (
              <ShortId value={sample.batch_id} />
            ) : null,
        },
        {
          key: 'stage',
          label: t('column.stage'),
          value: enumLabel('enum.sampleStage', sample.stage),
        },
        {
          key: 'module',
          label: t('row.currentModule'),
          value:
            done || sample.current_module == null || sample.current_module === '' ? null : (
              <span title={sample.current_module}>{moduleLabelOf(sample.current_module)}</span>
            ),
        },
      ].filter((row) => row.value != null)
    : []
  /** 정정 중에는 환자 번호가 입력칸이다 — 읽기 줄에서는 뺀다 */
  const listRowsWhileEditing = listRows.filter((row) => row.key !== 'patient')

  /*
   * ⚠ 완료면 「현재 자리」는 없다(수거 뒤 · §16-8). 배정 웰·묶음은 **이력**이라 남는다.
   * 🔴 서랍은 값이 있는 줄만, 셋 다 없으면 카드와 설명째 뺀다. 전체 페이지는 종전대로 `—`.
   */
  /*
   * 묶음의 웰 — 넘겨받았고 비지 않았고, **서버가 기록한 배정 웰이 그 안에 있을 때만.** 서버 값이 없거나 묶음 밖이면
   * 종전대로 서버 값 하나 — 묶음 대응은 아직 임시라(`temporarySampleGroups.ts`) 서버 기록과 어긋나면 서버를 따른다.
   */
  const serverWell = textOf(sample.assigned_well)
  const groupWells =
    assignedWells !== undefined && serverWell !== '' && assignedWells.includes(serverWell)
      ? assignedWells
      : undefined
  const placementRows = [
    {
      key: 'position',
      label: t('row.currentPosition'),
      value: done ? null : sample.current_position,
    },
    groupWells === undefined
      ? { key: 'assignedWell', label: t('row.assignedWell'), value: sample.assigned_well }
      : {
          key: 'assignedWell',
          label: t('row.assignedWell'),
          value: groupWells.join(' · '),
          title: t('row.assignedWell.server', { well: serverWell }),
        },
    { key: 'sampleRef', label: t('row.sampleRef'), value: sample.sample_ref },
  ]
    .filter((row) => !inDrawer || textOf(row.value) !== '')
    .map((row) => ({
      key: row.key,
      label: row.label,
      value: (
        <span className="mono" {...('title' in row ? { title: row.title } : {})}>
          {row.value == null || row.value === '' ? t('mask.none') : row.value}
        </span>
      ),
    }))

  const placementCard =
    placementRows.length > 0 ? (
      <Card title={placementTitle ?? t('card.placement')}>
        <ValueRows rows={placementRows} />
      </Card>
    ) : null

  const bodyCards = (
    <div className="fstack" ref={bodyRef}>
      {placementFirst ? placementCard : null}
      {editing ? (
        <Card title={t('card.sample')}>
          <div className="f2 sample-form">
            {/*
          🔴 **서랍에서는 시료 번호 칸을 두지 않는다**(eric 2026-09-23). 정정 대상이
             아니라 «읽기 전용 표시»뿐인데, 같은 값이 **행의 첫 칸**과 **이 상세의 제목**에
             이미 있었다 — 한 값을 세 번 읽게 하는 자리였다.
          ⚠ 전체 페이지에서는 남긴다. 거기엔 목록이 없어 제목 외에 값을 확인할 데가 없다.
        */}
            {inDrawer ? null : (
              <Field htmlFor={`${fieldIds}-id`} label={t('column.sampleId')}>
                {/* 시료 번호는 등록 때만 정한다. 정정 대상이 아니다 */}
                <TextInput
                  disabled
                  id={`${fieldIds}-id`}
                  disabledReason={t('reason.commandNotAllowed')}
                  onChange={() => undefined}
                  value={maskPii(sample.sample_id, visible)}
                  wide
                />
              </Field>
            )}
            <Field htmlFor={`${fieldIds}-patient`} label={t('column.patientId')}>
              <TextInput
                id={`${fieldIds}-patient`}
                onChange={edit('patient_id')}
                /*
                 * 🔴 **가려지면 마스크를, 보이면 «고치는 중인 값»을 보인다.** 둘을 한 칸이
                 *    쥐므로 마스크 상태에서는 입력이 비활성이다(`patientGate`).
                 */
                value={visible ? current.patient_id : maskPii(sample.patient_id, visible)}
                wide
                {...patientGate}
              />
            </Field>
            <Field htmlFor={`${fieldIds}-type`} label={t('field.sampleType')}>
              <SampleHintInput
                fieldGate={fieldGate}
                id={`${fieldIds}-type`}
                label={t('field.sampleType')}
                name="sample_type"
                onChange={edit('sample_type')}
                optionLabel={sampleTypeHintLabel}
                options={hints.sampleType}
                toggleLabel={t('sampleNew.hints.open', { field: t('field.sampleType') })}
                value={current.sample_type}
              />
            </Field>
          </div>
          {/* 서랍 — 고칠 수 없는 줄(작업 · 단계 · 현재 모듈)은 입력칸 아래 읽기 줄로 남는다(상태기계 소유) */}
          {listRowsWhileEditing.length > 0 ? <ValueRows rows={listRowsWhileEditing} /> : null}
        </Card>
      ) : listRows.length > 0 ? (
        <Card title={t('card.sample')}>
          <ValueRows rows={listRows} />
        </Card>
      ) : null}

      {editing ? (
        <Card title={t('card.testInfo')}>
          {/*
            🔴 빈 칸에 붉은 「미입력」을 달지 않는다(2026-10-01 회의 — 선택 입력이고 비어도 검사가 진행된다).
               종전 `error` 표시 셋을 걷었다.
          */}
          <div className="f2 sample-form">
            {/*
              🔴 적혈구 농도 칸은 없다 — 시료 추가 창에서 걷었다(eric 2026-10-04 · 2026-10-23 「왜 적혈구 농도가 있어?」).
                 정정 본문은 «바뀐 칸만» 싣는다(`correctionOf`) — 초안에 서버 값이 그대로 남아 보내지 않으니 옛 값이 지워지지 않는다.
            */}
            <Field htmlFor={`${fieldIds}-draw`} label={t('field.drawDate')}>
              <TextInput
                id={`${fieldIds}-draw`}
                onChange={edit('blood_draw_date')}
                value={current.blood_draw_date}
                {...fieldGate}
              />
            </Field>
            <Field htmlFor={`${fieldIds}-cancer`} label={t('field.cancerType')}>
              <SampleHintInput
                fieldGate={fieldGate}
                id={`${fieldIds}-cancer`}
                label={t('field.cancerType')}
                name="cancer_type"
                onChange={edit('cancer_type')}
                options={hints.cancerType}
                toggleLabel={t('sampleNew.hints.open', { field: t('field.cancerType') })}
                value={current.cancer_type}
              />
            </Field>
            <Field htmlFor={`${fieldIds}-stage`} label={t('field.cancerStage')}>
              <SampleHintInput
                fieldGate={fieldGate}
                id={`${fieldIds}-stage`}
                label={t('field.cancerStage')}
                name="cancer_stage"
                onChange={edit('cancer_stage')}
                options={hints.cancerStage}
                toggleLabel={t('sampleNew.hints.open', { field: t('field.cancerStage') })}
                value={current.cancer_stage}
              />
            </Field>
          </div>
        </Card>
      ) : identityInBody ? (
        identityRows.length + testInfoShown.length > 0 ? (
          <Card title={identityTitle ?? t('card.sample')}>
            <ValueRows rows={[...identityRows, ...testInfoShown]} />
          </Card>
        ) : null
      ) : (
        <Card title={t('card.testInfo')}>
          <ValueRows rows={testInfoShown} />
        </Card>
      )}

      {/*
        🔴 **「자리」 카드 — 두 축을 한 카드에 나란히, 낱말로 가른다**(2026-09-26).
           `current_position`(지금 자리 · 장비가 옮길 때마다 바뀐다) ↔ `assigned_well`(배치 생성 때
           정해져 불변). 둘이 달라도 결함이 아니다(`DM-REL-06`) — 그 구별은 **낱말**(「현재 자리」 · 「배정 웰」)이 맡는다.
           `sample_ref` 는 레시피 묶음 자리표(조회 때 파생)다.
        🔴 **카드 아래 설명 문단은 걷었다**(eric 2026-09-29 「현재 자리는 장비가 옮길 때마다 … 이런 텍스트 다 빼줘」).
           값도 상태도 아닌 해설이다 — 되살리지 마라.
        🔴 **서랍에도 선다** — 셋 다 목록 열에 없다(실측 0/349 · 전부 `—` 인 열을 깔지 않았다).
        ⚠ 완료면 「현재 자리」를 비운다(수거 뒤 · §16-8). 배정 웰·묶음은 **이력**이라 그대로 적는다.
        ⚠ 서랍은 값 없는 줄을 안 그린다(셋 다 없으면 카드째 뺀다 · eric 2026-09-27). 전체 페이지는 `—` 다.
           어느 쪽이든 웰 이름이나 `role` 로 메우지 않는다(계약 「대체값을 짓지 않는다」).
      */}
      {placementFirst ? null : placementCard}

      {editing ? (
        <Card title={t('card.correctionReason')}>
          <Field {...v.field('reason').fieldProps} label={t('field.reason')} required>
            <TextInput
              {...v.field('reason').inputProps}
              onChange={(next) => {
                setReason(next)
                v.change('reason', next)
              }}
              value={reason}
              wide
              {...fieldGate}
            />
          </Field>
        </Card>
      ) : null}
    </div>
  )

  const editActions = (
    <>
      {inDrawer ? (
        <Button onClick={closeEdit} size="pill">
          {t('action.cancel')}
        </Button>
      ) : null}
      <Button
        size="pill"
        variant="primary"
        {...saveGate}
        onClick={() => {
          if (!saveGate.disabled && v.validate(reason, bodyRef.current))
            onSave?.(sample.sample_id, patch, reason.trim())
        }}
      >
        {t('action.saveCorrection')}
      </Button>
    </>
  )

  return (
    <DetailPage
      onClose={onClose}
      footer={
        compactDrawer && editing ? (
          <div className="sample-correction-footer">
            {saveError ? <div role="alert">{saveError}</div> : null}
            {saveGate.disabled ? (
              <div className="sample-correction-footer__reason">{saveGate.disabledReason}</div>
            ) : null}
            <div className="btnrow btnrow--end">{editActions}</div>
          </div>
        ) : undefined
      }
      actions={
        <>
          {!editing && !hideCorrection && canReregisterSample(sample) && onReregister ? (
            <Button
              size="pill"
              {...(reregisterGate ?? gate(access, { perm: 'control' }))}
              onClick={() => onReregister(sample.sample_id)}
            >
              {t('sampleNew.reregister')}
            </Button>
          ) : null}
          {/*
            🔴 **조회성 동작이라 제어락·스트림 게이트를 걸지 않는다**(작업 상세 시료 탭과 같은 규율).
               권한(`view` — viewer 없음)만 본다. 집행은 서버 403 이다.
            ⚠ 「보고서 없음」 비활성은 두지 않는다 — 서버가 **존재하는 시료면 늘** 보고서를 준다
               (진행 중 · 미배정 · 폐기 포함 · 2026-09-28 실측 200). 이 서랍이 떴다면 시료가 있다.
          */}
          {hideReport ? null : (
            <Button
              size="pill"
              {...(onOpenReport === undefined
                ? { disabled: true, disabledReason: t('reason.demoSource') }
                : { ...viewGate(access, 'view'), onClick: () => onOpenReport(sample.sample_id) })}
            >
              {t('action.report')}
            </Button>
          )}
          {/*
            제목 줄에 세우는 표지 — 자리는 CSS(`sampleDetail.css`)가 옮긴다(표지 자체는 `display: none`).
            ⚠ 정정 중(취소 · 정정 저장 둘)은 걸지 않는다 — 단추 둘이 제목을 눌러 시료 번호가 음절마다 꺾였다(2026-10-02 실측).
          */}
          {hideReport && !hideCorrection && !editing ? (
            <span aria-hidden="true" className="sample-ahead--inline" />
          ) : null}
          {hideCorrection && !editing ? null : editing ? (
            compactDrawer ? null : (
              editActions
            )
          ) : (
            /* 🔴 권한이 없으면 숨기지 않고 비활성 + 사유다 */
            <Button onClick={openEdit} size="pill" variant="primary" {...fieldGate}>
              {t('action.correct')}
            </Button>
          )}
        </>
      }
      backLabel={t('screen.samples.title')}
      backTo={PATHS.samples}
      badge={
        statusChip ? (
          <span className="sample-status-chip">
            <StatusBadge flat label={sampleStateLabel(sample)} status={sample.status} />
          </span>
        ) : (
          <StatusBadge flat label={sampleStateLabel(sample)} status={sample.status} />
        )
      }
      submeta={
        /*
         * 🔴 **배치 UUID 는 앞 8자만 «보인다»**(2026-09-22 · 방안 A). 값은 그대로고
         *    전체는 `ShortId` 가 툴팁·보조기술로 내보낸다. 막바로 붙여 두면 부제 한 줄이
         *    식별자로 끝나서 환자·종류·단계가 밀린다.
         */
        compactDrawer ? undefined : (
          <span className="btnrow">
            {/* 🔴 값 있는 조각만 잇는다 — 빈 「— · —」 를 깔지 않는다(eric 2026-09-27) */}
            <span>
              {[
                identityInBody || listDrawer || textOf(sample.patient_id) === ''
                  ? null
                  : maskPii(sample.patient_id, visible),
                identityInBody || textOf(sample.sample_type) === '' ? null : sample.sample_type,
                listDrawer || omitMeta.includes('stage')
                  ? null
                  : enumLabel('enum.sampleStage', sample.stage),
              ]
                .filter((piece) => piece != null)
                .join(' · ')}
            </span>
            {!listDrawer &&
            !omitMeta.includes('batch') &&
            typeof sample.batch_id === 'string' &&
            sample.batch_id !== '' ? (
              <ShortId className="muted" value={sample.batch_id} />
            ) : null}
            {/*
            🔴 **서랍일 때만 붙는다**(eric 2026-09-23 「운전 카드는 너 말대로 해」). 「운전」 카드가
               통째로 빠지면서 **행에도 없던 두 값**이 갈 곳을 잃었다 — 버전과 폐기 사유다.
               전체 페이지는 카드가 그대로라 여기 또 적으면 같은 값이 두 번 선다.
            ⚠ 레시피·작업 상세의 메타 줄이 이미 `… v1` 꼴로 버전을 붙인다 — 같은 모양이다.
          */}
            {/*
            🔴 **현재 모듈 · 등록 시각 — 서랍일 때만**(2026-09-28 eric 「목록의 일부 열을 공간이 모자라면 숨겨야지」).
               서랍이 열리면 목록에서 이 두 열이 빠진다. 값 모양은 목록 칸과 같다(완료면 모듈 없음 · 원값은 툴팁).
            ⚠ 모니터링 서랍은 현재 모듈을 뺀다(`omitMeta` — 챔버 아래 한 마디가 이미 말한다).
          */}
            {inDrawer &&
            !listDrawer &&
            !omitMeta.includes('module') &&
            !done &&
            sample.current_module != null &&
            sample.current_module !== '' ? (
              <span title={sample.current_module}>
                {`${t('row.currentModule')} ${moduleLabelOf(sample.current_module)}`}
              </span>
            ) : null}
            {inDrawer && !omitMeta.includes('registered') && sample.created_at != null ? (
              <span className="muted">{`${t('column.registeredAt')} ${dateTimeText(sample.created_at)}`}</span>
            ) : null}
            {inDrawer && !omitMeta.includes('version') ? (
              <span className="mono">{`v${String(sample.version)}`}</span>
            ) : null}
            {inDrawer && sample.discard_reason != null ? (
              <span className="muted">{`${t('row.discardReason')} ${sample.discard_reason}`}</span>
            ) : null}
          </span>
        )
      }
      title={
        titleLead === undefined ? (
          maskPii(titleText ?? sample.sample_id, visible)
        ) : (
          <>
            <span className="ahead__lead">{titleLead}</span>{' '}
            {maskPii(titleText ?? sample.sample_id, visible)}
          </>
        )
      }
    >
      <OfflineNotice shown={access.offline === true} />
      {notice ? <p role="status">{notice}</p> : null}
      {reregisterError ? <div role="alert">{reregisterError}</div> : null}
      {!compactDrawer && editing && saveError ? <div role="alert">{saveError}</div> : null}
      {/*
        🔴 **서랍에서는 오른쪽 열 자체가 없다**(eric 2026-09-23 「운전 카드는 너 말대로 해」).
           「운전」 카드의 여섯 줄 중 다섯이 목록 행에 이미 있어서, 남는 것이 버전 한 줄뿐이었다 —
           카드 한 장을 세우자고 300px 열을 잡아 둘 값이 아니다. 남은 둘(버전 · 폐기 사유)은
           머리의 메타 줄로 올렸다. 카드 세 장이 한 열로 폭을 다 쓴다.
        🔴 **전체 페이지(장비 관점 콘솔·태블릿)는 2열 그대로다** — 옆에 목록이 없어 여섯 줄이
           전부 필요하다. 갈리는 축은 `onClose`(= 서랍인가) 하나이고 시험이 양쪽을 못 박는다.
      */}
      {inDrawer ? (
        lead === undefined && trail === undefined ? (
          bodyCards
        ) : (
          <div className="fstack">
            {lead}
            {bodyCards}
            {trail}
          </div>
        )
      ) : (
        <div className="detail2">
          {bodyCards}

          <div className="fstack fstack--sticky">
            <Card title={t('card.operation')}>
              <ValueRows
                rows={[
                  {
                    key: 'status',
                    label: t('column.status'),
                    value: (
                      <StatusBadge flat label={sampleStateLabel(sample)} status={sample.status} />
                    ),
                  },
                  {
                    key: 'stage',
                    label: t('column.stage'),
                    value: enumLabel('enum.sampleStage', sample.stage),
                  },
                  {
                    key: 'batch',
                    label: t('column.batch'),
                    value: <span className="mono">{orDash(sample.batch_id)}</span>,
                  },
                  {
                    key: 'module',
                    label: t('row.currentModule'),
                    value:
                      done || sample.current_module == null ? (
                        t('mask.none')
                      ) : (
                        <span title={sample.current_module}>
                          {moduleLabelOf(sample.current_module)}
                        </span>
                      ),
                  },
                  // 「현재 자리」는 왼쪽 「자리」 카드가 배정 웰과 나란히 적는다 — 여기 또 적지 않는다
                  {
                    key: 'createdAt',
                    label: t('column.registeredAt'),
                    value: dateTimeText(sample.created_at),
                  },
                  {
                    key: 'version',
                    label: t('column.version'),
                    value: <span className="mono">{`v${String(sample.version)}`}</span>,
                  },
                  ...(sample.discard_reason == null
                    ? []
                    : [
                        {
                          key: 'discard',
                          label: t('row.discardReason'),
                          value: sample.discard_reason,
                        },
                      ]),
                ]}
              />
            </Card>
          </div>
        </div>
      )}
      {reportPanel === undefined ? null : <SampleReportModal {...reportPanel} view={visible} />}
    </DetailPage>
  )
}

/**
 * 선택 입력 칸의 값 — 비었으면 흐린 「NA」(2026-10-01 CTCells 회의 「빈칸은 NA 처리」). 시료 목록 · 시료 서랍이 같이 쓴다.
 * 🔴 경고가 아니다 — 주의색 · 글리프를 얹지 마라. 숫자 `0` 은 값이다.
 * ⚠ 계약 `Sample` 에 자리가 있는 «입력 칸»에만 쓴다. 응답에 없는 값의 자리를 이것으로 새로 만들지 마라.
 */
export function NaOr({ value }: { value: string | number | null | undefined }) {
  const text = textOf(value)
  return text === '' ? <span className="muted">{t('value.na')}</span> : <>{text}</>
}

/** 보기 줄의 글자 — 비었으면 `''`(그 줄을 그리지 않는다). 숫자 `0` 은 값이다 */
function textOf(value: string | number | null | undefined): string {
  return value == null ? '' : String(value).trim()
}
